import type { SeasonResponse } from '@cradle7/shared';
import { useSyncExternalStore } from 'react';
import { api } from './api';

/**
 * Phase 3l — season store. Plain-module + emit pattern mirroring
 * `challengeStore` / `dailyStatusStore` so React components can subscribe via
 * `useSyncExternalStore` and bootAuth can hydrate via `setSeasonData`.
 *
 * Failure of the underlying fetch is non-fatal: the SeasonStrip degrades to
 * a loading skeleton rather than blocking the boot path.
 */

let cache: SeasonResponse | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((fn) => fn());
}

export function getSeasonData(): SeasonResponse | null {
  return cache;
}

export function setSeasonData(s: SeasonResponse | null): void {
  cache = s;
  emit();
}

export function subscribeSeasonData(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Self-fetch helper used by `bootAuth` so the season strip is warm on first
 * render. Swallows errors — the caller should already be tolerating null.
 */
export async function hydrateSeason(): Promise<void> {
  try {
    const resp = await api.getSeason();
    cache = resp;
    emit();
  } catch (err) {
    // Non-fatal: SeasonStrip will show its loading skeleton.
    console.warn('[seasonStore] hydrate failed', err);
  }
}

/** Test hook: clear in-memory state + listeners. */
export function _resetSeasonStore(): void {
  cache = null;
  listeners.clear();
}

let snapshotKey: SeasonResponse | null = null;
let snapshotCache: SeasonResponse | null = null;

function getSnapshot(): SeasonResponse | null {
  if (snapshotKey === cache) return snapshotCache;
  snapshotKey = cache;
  snapshotCache = cache;
  return cache;
}

export function useSeasonData(): SeasonResponse | null {
  return useSyncExternalStore(subscribeSeasonData, getSnapshot, getSnapshot);
}
